import React from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Role } from '@/types/Role';
import type { RoleLiveValues } from './types';
import { calculateHalfDayRate } from '@/utils/calculateHalfDayRate';
import { formatCurrency } from '@/utils/formatters';
import { billingBadgeBase, billingBadgeVariant } from './roleFormStyles';

interface RoleListItemProps {
  role: Role;
  index: number;
  isExpanded?: boolean;
  onCardClick: (index: number) => void;
  /** When provided, display these values instead of the saved role (for live editing) */
  liveOverrides?: RoleLiveValues;
}

export const RoleListItem = React.memo(function RoleListItem({
  role,
  index,
  isExpanded,
  onCardClick,
  liveOverrides,
}: RoleListItemProps) {
  const displayName = liveOverrides?.name ?? role.name;
  const displayDescription = liveOverrides?.description ?? role.description;
  const displayBillingStructure = liveOverrides?.billingStructure ?? role.billingStructure;
  const displayRate = liveOverrides?.rate ?? role.rate;
  const isDayRate = displayBillingStructure === 'day_rate';
  const halfDayRate = liveOverrides ? null : calculateHalfDayRate(role);
  const liveHalfDayRate = liveOverrides?.headerHalfDayRate;

  return (
    <button
      type="button"
      aria-expanded={!!isExpanded}
      aria-label={`Edit role ${displayName}`}
      className={cn(
        'group w-full text-left grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 px-3 py-3 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isExpanded
          ? 'bg-surface-2 dark:bg-transparent border-b border-border/40'
          : 'bg-surface-1 dark:bg-transparent border border-border/60 equipment-card rounded hover:-translate-y-[1px] hover:shadow-md hover:border-primary/30 active:translate-y-[1px] active:shadow-none transition-[background-color,transform,box-shadow,border-color] duration-150'
      )}
      onClick={() => onCardClick(index)}
    >
      {/* Row 1, Col 1: Name */}
      <div className="font-heading text-sm uppercase tracking-wide truncate text-foreground self-center">
        {displayName}
      </div>

      {/* Row 1, Col 2: Rate with unit */}
      <div className="font-mono text-sm font-bold tabular-nums text-primary-text self-center text-right">
        {displayRate ? formatCurrency(displayRate, { showCents: false }) : ''}
        {displayRate && (
          <span className="text-[10px] sm:text-[12px] font-normal opacity-60">
            /{isDayRate ? 'day' : 'hr'}
          </span>
        )}
      </div>

      {/* Row 1–2, Col 3: Chevron spanning both rows */}
      <div className="row-span-2 flex items-center justify-center w-4">
        {isExpanded ? (
          <ChevronDown aria-hidden="true" className="h-4 w-4 text-primary-text" />
        ) : (
          <ChevronRight aria-hidden="true" className="h-4 w-4 text-muted-foreground/80 group-active:text-primary-text transition-colors" />
        )}
      </div>

      {/* Row 2, Col 1: Description */}
      <div className={cn("text-xs text-muted-foreground truncate self-center", !displayDescription && "italic")}>
        {displayDescription || 'No description'}
      </div>

      {/* Row 2, Col 2: Badges */}
      <div className="flex items-center gap-2 self-center justify-end">
        {/* Billing type badge */}
        {displayBillingStructure && (
          <span className={cn(billingBadgeBase, billingBadgeVariant(displayBillingStructure))}>
            {isDayRate ? 'Day Rate' : 'Hourly'}
          </span>
        )}

        {/* Half-day badge */}
        {halfDayRate !== null && (
          <span className={cn(billingBadgeBase, 'bg-primary/10 text-primary-text border border-primary/20')}>
            <span className="lg:hidden">Half day</span>
            <span className="hidden lg:inline">&frac12; {formatCurrency(halfDayRate, { showCents: false })}</span>
          </span>
        )}
        {liveHalfDayRate && (
          <span className={cn(billingBadgeBase, billingBadgeVariant('day_rate'), 'whitespace-nowrap')}>
            &frac12; {liveHalfDayRate}
          </span>
        )}
      </div>
    </button>
  );
});
